import backgroundChecksService from '@/api/background-checks-service';
import { removeArrayItem, updateArrayItem } from '@/util/array';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { mapErrorsToInputs } from '@/util/forms';

const getDefaultFormItem = () => ({});

export const initialBackgroundChecksFilterParams = () => ({});

const state = {
  backgroundChecks: null,
  backgroundChecksPagination: {},
  newBackgroundCheck: getDefaultFormItem(),
  editedBackgroundCheck: {},
  backgroundCheckValidationErrors: {},
  backgroundChecksFilterParams: initialBackgroundChecksFilterParams(),
};

const mutations = {
  SET_BACKGROUND_CHECKS(state, { data, current_page, per_page, total }) {
    state.backgroundChecks = data;
    state.backgroundChecksPagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_FILTER_PARAMS(state, params) {
    state.backgroundChecksFilterParams = params;
  },

  STORE_BACKGROUND_CHECK(state, backgroundCheck) {
    state.backgroundChecks = [...state.backgroundChecks, backgroundCheck];
    state.newBackgroundCheck = getDefaultFormItem();
    state.backgroundCheckValidationErrors = {};
    state.backgroundChecksPagination.total += 1;
  },

  SET_EDITED_BACKGROUND_CHECK(state, backgroundCheck) {
    state.backgroundCheckValidationErrors = {};
    state.editedBackgroundCheck = JSON.parse(JSON.stringify(backgroundCheck));
  },

  UPDATE_BACKGROUND_CHECK(state, backgroundCheck) {
    state.backgroundChecks = updateArrayItem(state.backgroundChecks, backgroundCheck);
  },

  DELETE_BACKGROUND_CHECK(state, backgroundCheck) {
    state.backgroundChecks = removeArrayItem(state.backgroundChecks, backgroundCheck);
    state.backgroundChecksPagination.total -= 1;
  },

  SET_BACKGROUND_CHECK_VALIDATION_ERRORS(state, validationErrors) {
    state.backgroundCheckValidationErrors = validationErrors;
  },

  RESET_BACKGROUND_CHECK_VALIDATION_ERRORS(state) {
    state.backgroundCheckValidationErrors = {};
  },

  CLEAR_VALIDATION_ERRORS(state, field) {
    delete state.backgroundCheckValidationErrors[field];
  },
};

const actions = {
  fetchBackgroundChecks({ commit }, params) {
    commit('SET_FILTER_PARAMS', params);
    return backgroundChecksService.getPage(params).then((res) => {
      commit('SET_BACKGROUND_CHECKS', res.data);
      return res.data;
    });
  },

  storeBackgroundCheck({ commit }, backgroundCheck) {
    return backgroundChecksService
      .create(backgroundCheck)
      .then((res) => {
        commit('STORE_BACKGROUND_CHECK', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('data_created'));
        return res;
      })
      .catch((err) => {
        commit('SET_BACKGROUND_CHECK_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  editBackgroundCheck({ state, commit }, backgroundCheckId) {
    const backgroundCheck = state.backgroundChecks?.find((s) => s.id === backgroundCheckId);
    if (backgroundCheck) {
      commit('SET_EDITED_BACKGROUND_CHECK', backgroundCheck);
      return Promise.resolve(backgroundCheck);
    }
    return backgroundChecksService.getById(backgroundCheckId).then((res) => {
      commit('SET_EDITED_BACKGROUND_CHECK', res.data);
      return res.data;
    });
  },

  updateBackgroundCheck({ commit }, backgroundCheck) {
    return backgroundChecksService
      .update(backgroundCheck)
      .then((res) => {
        commit('UPDATE_BACKGROUND_CHECK', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('data_updated'));
      })
      .catch((err) => {
        commit('SET_BACKGROUND_CHECK_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  deleteBackgroundCheck({ commit }, backgroundCheck) {
    openConfirmDialog({
      title: i18n.t('confirm_data_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      backgroundChecksService.delete(backgroundCheck).then(() => {
        commit('DELETE_BACKGROUND_CHECK', backgroundCheck);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('data_deleted'));
      });
    });
  },

  clearBackgroundCheckValidationErrors({ commit }, field) {
    commit('CLEAR_VALIDATION_ERRORS', field);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
